"use client";

import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import TextType from "../components/TextType";
import ConsultationForm from "../components/ConsultationForm";
import { getHeroContentFromPath } from "../data/hero";

export default function HeroSection(props) {
  const pathname = usePathname();
  const [heroData, setHeroData] = useState(null);

  useEffect(() => {
    // If dynamicData is already passed from parent, use it and don't fetch
    if (props.dynamicData) {
      setHeroData(props.dynamicData);
      return;
    }

    const parts = pathname.split("/").filter(Boolean);
    const servicesIndex = parts.indexOf("services");
    const slug = servicesIndex !== -1 ? parts[servicesIndex + 1] : parts[parts.length - 1];

    if (slug && slug !== "services") {
      const fetchHero = async () => {
        try {
          const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
          const res = await fetch(`${apiUrl}/api/service/${slug}`);

          if (res.status === 404) return;
          if (!res.ok) return;

          const data = await res.json();
          if (data.success && data.data?.hero) {
            setHeroData(data.data.hero);
          }
        } catch (err) {
          if (err.name === 'TypeError') {
            console.warn("HeroSection: API server unreachable. Using fallbacks.");
          }
        }
      };
      fetchHero();
    }
  }, [pathname, props.dynamicData]);

  const { title: detectedTitle, typingTexts: detectedTyping } =
    getHeroContentFromPath(pathname);

  const title = heroData?.title ?? props?.title ?? detectedTitle;
  const typingTexts =
    (heroData?.subtitle ? [heroData.subtitle] : null) ??
    props?.typingTexts ??
    detectedTyping;
  const bulletsFromData = getHeroContentFromPath(pathname)?.bullets;
  const bullets =
    heroData?.highlights ??
    props?.bullets ??
    bulletsFromData ?? [
      "Custom Web Design & Development",
      "Creative UX/UI and SEO Optimised Design",
      "Responsive and Mobile First Website",
      "Integration and Migration",
      "Secure and Robust Development",
      "Ongoing Support and Maintenance",
    ];
  const isFormEnabled = heroData?.form?.enabled ?? true;

  return (
    <section
      className="py-5 py-lg-6"
      style={{
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.9) 100%)",
      }}
    >
      <div className="container">
        <div className="row g-4 align-items-center">
          {/* Left: headline and bullet points */}
          <div className={isFormEnabled ? "col-12 col-lg-7" : "col-12 col-lg-12"}>
            <p
              className="text-uppercase mb-1"
              style={{ letterSpacing: ".08em", color: "#F48428" }}
            >
              {title}
            </p>

            {/* Animated typing headline */}
            <TextType
              as="h1"
              texts={typingTexts}
              typingSpeed={70}
              deletingSpeed={35}
              pauseDuration={1200}
              showCursor
              cursorChar="_"
              startOnVisible
              loop={false}
              hideCursorOnComplete
              className="display-5 fw-bold text-white mb-4"
              style={{ lineHeight: 1.15 }}
              data-reveal="slide-up"
            />

            {/* Bullet list */}
            <div className={`row g-3 mt-5 ${isFormEnabled ? "" : "justify-content-center"}`} data-reveal="stagger-up">
              {bullets.map((txt) => (
                <div className={isFormEnabled ? "col-12 col-sm-6" : "col-12 col-sm-4"} key={txt}>
                  <div className="d-flex align-items-start gap-2">
                    <i
                      className="fa-regular fa-circle-check mt-1"
                      style={{ color: "#F48428" }}
                    ></i>
                    <span style={{ color: "#fff" }}>{txt}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form card */}
          {isFormEnabled && (
            <div className="col-12 col-lg-5">
              <ConsultationForm
                title={heroData?.form?.title}
                buttonText={heroData?.form?.buttonText}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
